import { useEffect, useMemo, useState } from "react";
import { AdminImport } from "../rosterImportApiSlice";
import IssueCard from "../summary/IssueCard";
import ParseIssuesPreview from "./ParseIssuesPreview";

export type ParseCategory = "courses" | "teachers" | "students";
export type ParseIssuesByCategory = Record<ParseCategory, string[]>;
export type ParseIssueCounts = Record<ParseCategory, number>;

interface ParseDecisionPanelProps {
	importModel: AdminImport;
}

const LABELS: Record<ParseCategory, { plural: string; singular: string }> = {
	courses: { plural: "Courses", singular: "Course" },
	teachers: { plural: "Teachers", singular: "Teacher" },
	students: { plural: "Students", singular: "Student" },
};

const CATEGORIES: ParseCategory[] = ["courses", "teachers", "students"];

export default function ParseDecisionPanel({ importModel }: ParseDecisionPanelProps) {
	const [openCategory, setOpenCategory] = useState<ParseCategory | null>(null);

	const { parseIssuesByCategory, issueCounts, rowWithIssueCounts } = useMemo(
		() => buildParseIssuesByCategory(importModel),
		[importModel]
	);

	function buildParseIssuesByCategory(model?: AdminImport): {
		parseIssuesByCategory: ParseIssuesByCategory;
		issueCounts: ParseIssueCounts;
		rowWithIssueCounts: ParseIssueCounts;
	} {
		const emptyIssues: ParseIssuesByCategory = { courses: [], teachers: [], students: [] };
		const emptyCounts: ParseIssueCounts = { courses: 0, teachers: 0, students: 0 };
		if (!model)
			return {
				parseIssuesByCategory: emptyIssues,
				issueCounts: emptyCounts,
				rowWithIssueCounts: emptyCounts,
			};

		const build = (
			items: Array<{ row: number; isValid: boolean; errors: string[] }> = []
		): { issues: string[]; count: number; rowCount: number } => {
			const issues: string[] = [];
			let count = 0;
			let rowCount = 0;
			for (const item of items) {
				if (!item.isValid) {
					rowCount++;
					issues.push(`Row ${item.row} is not valid:`);
					count += item.errors?.length ?? 0;
					for (const e of item.errors ?? []) issues.push(`- ${e}`);
				}
			}
			return { issues, count, rowCount };
		};

		const coursesResult = build(model.courses as any);
		const teachersResult = build(model.teachers as any);
		const studentsResult = build(model.students as any);

		return {
			//all issues
			parseIssuesByCategory: {
				courses: coursesResult.issues,
				teachers: teachersResult.issues,
				students: studentsResult.issues,
			},
			//how many issues there are across all rows
			issueCounts: {
				courses: coursesResult.count,
				teachers: teachersResult.count,
				students: studentsResult.count,
			},
			//how many rows have at least one issue
			rowWithIssueCounts: {
				courses: coursesResult.rowCount,
				teachers: teachersResult.rowCount,
				students: studentsResult.rowCount,
			},
		};
	}

	const anyIssues =
		issueCounts.courses > 0 || issueCounts.teachers > 0 || issueCounts.students > 0;

	const openIssues = openCategory ? parseIssuesByCategory[openCategory] : [];
	const openLabel = openCategory ? LABELS[openCategory].singular : null;

	const autoOpenCategory =
		(issueCounts.courses > 0 ? "courses" : null) ??
		(issueCounts.teachers > 0 ? "teachers" : null) ??
		(issueCounts.students > 0 ? "students" : null);

	useEffect(() => {
		setOpenCategory((autoOpenCategory as ParseCategory | null) ?? null);
	}, [autoOpenCategory]);

	const SECTION_TITLE = "Roster Analysis";
	const SECTION_DESCRIPTION = anyIssues
		? "Issues were found! Click a category below to view any issues. We recommend that you reconcile these issues and re-upload the roster before continuing. If you continue, all rows with issues will not be imported."
		: "Verify the valid counts for each category before continuing.";

	return (
		<div>
			<h2>{SECTION_TITLE}</h2>
			<p className="text-sm text-gray-600">{SECTION_DESCRIPTION}</p>
			<div className="mt-2 grid grid-cols-1 sm:grid-cols-3">
				{CATEGORIES.map((cat) => {
					const label = LABELS[cat].plural;
					const issueCount = issueCounts[cat];

					const valueText =
						cat === "courses"
							? `${importModel.courseCount.success}/${importModel.courseCount.total} valid`
							: cat === "teachers"
								? `${importModel.teacherCount.success}/${importModel.teacherCount.total} valid`
								: `${importModel.studentCount.success}/${importModel.studentCount.total} valid`;
					return (
						<IssueCard
							key={cat}
							title={label}
							subtitle={valueText}
							issueCount={issueCount}
							isOpen={openCategory === cat}
							onToggle={() => setOpenCategory(cat)}
							disableWhenEmpty
							ariaLabel={`Toggle ${label} issues`}
							titleAttr={`Click to view ${label.toLowerCase()} issues`}
						/>
					);
				})}
			</div>
			{openLabel && openCategory && (
				<ParseIssuesPreview
					title={`${openLabel}`}
					issues={openIssues}
					issueCount={issueCounts[openCategory]}
					rowsWithIssueCount={rowWithIssueCounts[openCategory]}
				/>
			)}
		</div>
	);
}
